<template>
  <div class="settings-view">
    <div class="settings-header">
    <div class="breadcrumb">Home / Settings</div>
    <div class="header-row">
          <button @click="goBack" class="back-circle" aria-label="Go back">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m12 19-7-7 7-7"/>
            </svg>
          </button>
          <h1>Settings</h1>
        </div>
      </div>
    
    <div class="settings-content">
      <div class="settings-section">
        <div class="section-label">ACCOUNT</div>
        <div class="card">
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">Signed in as</div>
              <div class="setting-value">{{ userEmail || 'Unknown User' }}</div>
            </div>
          </div>

          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">Session</div>
              <div class="setting-description">Sign out of this device</div>
            </div>
            <div class="setting-control">
              <button class="ghost-btn" @click="logout">Sign Out</button>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-label">INTEGRATIONS</div>
        <div class="card">
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label" for="graph-access">Microsoft Graph Access</div>
              <div class="setting-description">Allow access to Microsoft 365 data (calendar, mail, files)</div>
            </div>
            <div class="setting-control">
              <label class="switch">
                <input 
                  id="graph-access"
                  type="checkbox" 
                  v-model="graphAccessEnabled" 
                  @change="handleGraphAccessToggle"
                  :disabled="isProcessing"
                />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <div v-if="isProcessing" class="status-message processing">
            <div class="spinner"></div>
            {{ statusMessage }}
          </div>

          <div v-if="errorMessage" class="status-message error">
            ⚠️ {{ errorMessage }}
          </div>

          <div v-if="graphAccessEnabled && !isProcessing && !errorMessage" class="status-message success">
            ✅ Graph access is enabled
          </div>

          <div v-if="graphAccessEnabled && !isProcessing" class="permissions-info">
            <details>
              <summary>View granted permissions</summary>
              <ul>
                <li>Read your profile information</li>
                <li>Read your calendar events</li>
                <li>Read your emails</li>
                <li>Access your files in OneDrive</li>
              </ul>
            </details>
          </div>
        </div>
      </div>

      <div class="settings-section">
        <div class="section-label">DEVELOPER MODE</div>
        <div class="card">
          <div class="setting-row">
            <div class="setting-info">
              <div class="setting-label">Enable developer features</div>
              <div class="setting-description">Show developer-only features in the UI</div>
            </div>
            <div class="setting-control">
              <label class="switch">
                <input type="checkbox" v-model="developerEnabled" />
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/composables/useAuth";

const router = useRouter();
const { user, enableGraphAccess, getGraphToken, isAuthenticated, initializeMsal, logout } = useAuth();

const graphAccessEnabled = ref(false);
const isProcessing = ref(false);
const errorMessage = ref("");
const statusMessage = ref("");

const userEmail = computed(() => user.value?.username || "");
const developerEnabled = ref(false);

// Check if Graph access is already enabled when component mounts
onMounted(async () => {
  // Ensure MSAL is initialized and only check Graph access if user is authenticated
  await initializeMsal();
  if (isAuthenticated.value) {
    await checkGraphAccess();
  } else {
    graphAccessEnabled.value = false;
  }
});

async function checkGraphAccess() {
  // Guard: don't attempt token acquisition if not authenticated
  if (!isAuthenticated.value) {
    graphAccessEnabled.value = false;
    return;
  }

  try {
    // Try to get a Graph token silently - if successful, Graph access is enabled
    await getGraphToken();
    graphAccessEnabled.value = true;
  } catch (error) {
    // If it fails, Graph access is not enabled - this is expected
    graphAccessEnabled.value = false;
  }
}

async function handleGraphAccessToggle() {
  if (!graphAccessEnabled.value) {
    // User is trying to disable - just update the UI
    // Note: In a real app, you might want to call a backend API to revoke consent
    return;
  }

  // User is trying to enable Graph access
  isProcessing.value = true;
  errorMessage.value = "";
  statusMessage.value = "Requesting Microsoft Graph access...";

  try {
    const result = await enableGraphAccess();
    
    if (result.success) {
      statusMessage.value = "Graph access enabled successfully!";
      setTimeout(() => {
        isProcessing.value = false;
        statusMessage.value = "";
      }, 2000);
    } else {
      if (result.error?.includes("Redirecting")) {
        statusMessage.value = "Redirecting to Microsoft for consent...";
        // Don't reset processing state - page will redirect
      } else {
        graphAccessEnabled.value = false;
        errorMessage.value = result.error || "Failed to enable Graph access";
        isProcessing.value = false;
        statusMessage.value = "";
      }
    }
  } catch (error) {
    graphAccessEnabled.value = false;
    errorMessage.value = "An error occurred while enabling Graph access";
    isProcessing.value = false;
    statusMessage.value = "";
    console.error("Graph access toggle error:", error);
  }
}

function goBack() {
  router.push({ name: "home" });
}
</script>

<style scoped>
.settings-view {
  margin: 0;
  padding: 1.5rem;
  min-height: calc(100vh - 60px); /* Account for TopNav height */
  width: 100%;
}

.settings-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.breadcrumb {
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 0.25rem;
  display: block;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.back-circle {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #eef2f6;
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
  cursor: pointer;
  margin-right: 2px;
}

.header-row h1 {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
}

.section-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 6px 18px rgba(15,23,42,0.04);
  margin-bottom: 1rem;
  max-width: none;
  width: 100%;
}

.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px dashed rgba(0,0,0,0.04);
}

.setting-row:last-child { border-bottom: none; }

.ghost-btn {
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0078d4;
  background: transparent;
  border: 1px solid #0078d4;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.back-button:hover {
  background: #0078d4;
  color: white;
}

.back-button:focus {
  outline: 2px solid #0078d4;
  outline-offset: 2px;
}

.settings-header h1 {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #333333;
}

.settings-content {
  width: 100%;
  max-width: none;
  margin: 0 auto;
  max-width: 900px;
}

.settings-section {
  margin-bottom: 2rem;
}

.setting-info {
  flex: 1;
}

.setting-label {
  display: block;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.setting-description {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.setting-value {
  font-family: "Inter", sans-serif;
  font-size: 16px;
  color: #111827;
  font-weight: 500;
}

.setting-control {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/* Toggle Switch Styles */
.switch {
  position: relative;
  display: inline-block;
  width: 48px;
  height: 28px;
  cursor: pointer;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  max-width: none;
  width: 100%;
  background-color: #d1d5db;
  transition: 0.2s;
  border-radius: 28px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

input:checked + .slider {
  background-color: #0078d4;
}

input:focus + .slider {
  box-shadow: 0 0 0 3px rgba(0, 120, 212, 0.2);
}

input:checked + .slider:before {
  transform: translateX(20px);
}

input:disabled + .slider {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Status Messages */
.status-message {
  display: flex;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-size: 14px;
}

.status-message.processing {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.status-message.error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.status-message.success {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #1d4ed8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.permissions-info {
  margin-top: 1rem;
  font-size: 14px;
}

.permissions-info details {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  background: #f9fafb;
}

.permissions-info summary {
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0.5rem;
}

.permissions-info[open] summary {
  margin-bottom: 1rem;
}

.permissions-info ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #6b7280;
}

.permissions-info li {
  margin-bottom: 0.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive design */
@media (max-width: 768px) {
  .settings-view {
    padding: 1rem;
  }
  
  .setting-row {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .setting-control {
    align-self: flex-start;
  }
}
</style>